//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { useQueryClient } from '@tanstack/react-query';
import angular, { IScope } from 'angular';
import React, { useRef, useState } from 'react';
import { useSelector, useStore } from 'react-redux';

import {
  ModeratorBulkMediaAction,
  ModeratorBulkScope,
  hasCurrentModeratorBulkWriteAccess,
  useModeratorBulkMediaMutation,
} from '@app/bulk-actions/hooks/useModeratorBulkMediaMutation';
import {
  getActiveModeratorListData,
  partitionModeratorForceReviewSelection,
  partitionModeratorUnshareSelection,
  useModeratorUnshareSelection,
} from '@app/bulk-actions/hooks/useModeratorUnshareSelection';
import { BulkActionNames } from '@app/bulk-actions/model/BulkActionNames';
import { getModeratorBulkActionsAccess } from '@app/bulk-actions/model/moderatorBulkActionsAccess';
import { getBulkActionsSelectedItems } from '@app/bulk-actions/selectors/bulk-actions-selector';
import { UiaBulkActionsTrackingIds } from '@app/bulk-actions/uia-bulk-actions/uiaBulkActionsTrackingIds';
import { Alert } from '@components/atoms/alert/Alert';
import Icon from '@components/atoms/icon/Icon';
import ActionSheet from '@components/molecules/action-sheet/ActionSheet';
import { Modal } from '@components/molecules/modal/Modal';
import { SmallTooltip } from '@components/molecules/tooltip/Tooltip';
import { useCurrentStateAndParams } from '@hooks/useCurrentStateAndParams';
import { MediaLibraryQueryParams } from '@media-library/constants/mediaLibraryConstants';
import { ICuralateStore } from '@models/AppState';
import Cuid from '@models/Cuid';
import { Permissions } from '@models/Permissions';
import { useModeratorSurface } from '@moderation-mode/hooks/useModeratorSurface';
import makeAngularReactControllerWithScope, {
  applyScope,
} from '@react-bridge/angular-react-widget-with-scope';
import { getClientId } from '@redux/selectors/getClientId';
import { pluralize } from '@utils/stringUtils';
import { withTracking } from '@utils/withTracking';

interface Props {
  downloadDisabledReason: false | string;
  onDownload: () => void;
}

const CONFIRMATION_ACTIONS = {
  REMOVE: 'remove',
  REQUEST_REVIEW: 'request-review',
} as const;

type ConfirmationAction =
  (typeof CONFIRMATION_ACTIONS)[keyof typeof CONFIRMATION_ACTIONS];

const BULK_MEDIA_ACTIONS = {
  SHARE: 'share',
  REQUEST_REVIEW: 'request-review',
  UNSHARE: 'unshare',
  REMOVE_FROM_STREAM: 'remove-from-stream',
} as const satisfies Record<string, ModeratorBulkMediaAction>;

const NO_PERMISSION_MESSAGE =
  "You don't have permission to perform this action.";

export const ModeratorBulkActions = ({
  downloadDisabledReason,
  onDownload,
}: Props) => {
  const session = useSelector((store: ICuralateStore) => store.session);
  const clientId = useSelector(getClientId);
  const selectedItems = useSelector(getBulkActionsSelectedItems);
  const currentCollection = useSelector(
    (store: ICuralateStore) => store.bulkActions.currentCollection,
  );
  const disabledActions = useSelector(
    (store: ICuralateStore) => store.bulkActions.disabledActions,
  );
  const { params, state } =
    useCurrentStateAndParams<MediaLibraryQueryParams>();
  const { isModeratorSurface } = useModeratorSurface();
  const access = getModeratorBulkActionsAccess(
    session,
    isModeratorSurface,
    state,
  );
  const mutation = useModeratorBulkMediaMutation();
  const store = useStore<ICuralateStore>();
  const queryClient = useQueryClient();
  const selectedCuids = [
    ...new Set(
      selectedItems.map((item) => Cuid.fromPartial(item.cuid, clientId)),
    ),
  ];
  const removalSelection = useModeratorUnshareSelection(
    selectedCuids,
    clientId,
    access.isAllSharedMedia,
  );
  // The existing removal hook above subscribes to this same active list.
  // No new query, subscription, or shared selection fields are needed.
  const requestReviewData = access.isAllSharedMedia
    ? getActiveModeratorListData(queryClient)
    : undefined;
  const requestReviewSelection = partitionModeratorForceReviewSelection(
    selectedCuids,
    requestReviewData?.pages.flatMap((page) => page.contentItems) ?? [],
    clientId,
  );
  const submittingRef = useRef(false);
  const [confirmationAction, setConfirmationAction] =
    useState<ConfirmationAction | null>(null);

  if (!access.isEnabled || selectedItems.length === 0) {
    return null;
  }

  const scope: ModeratorBulkScope = {
    clientId,
    stateName: state,
    collectionId: params.collectionId,
  };
  const itemCount = selectedItems.length;
  const items = pluralize(itemCount, 'item');
  const isStreamRemovalUnavailable =
    access.isStream &&
    (currentCollection?.id !== params.collectionId ||
      disabledActions.includes(BulkActionNames.REMOVE_FROM_COLLECTION));
  const isWriteDisabled = !access.canWrite || mutation.isPending;

  const isAllSharedRemoval =
    confirmationAction === CONFIRMATION_ACTIONS.REMOVE &&
    access.isAllSharedMedia;
  const isAllSharedRequestReview =
    confirmationAction === CONFIRMATION_ACTIONS.REQUEST_REVIEW &&
    access.isAllSharedMedia;
  const requestReviewCount = requestReviewSelection.eligibleCuids.length;
  const noEligibleReviewMedia =
    isAllSharedRequestReview && requestReviewCount === 0;
  const eligibleCount = removalSelection.eligibleCuids.length;
  const noEligibleMedia = isAllSharedRemoval && eligibleCount === 0;

  const submit = async (action: ModeratorBulkMediaAction) => {
    if (
      submittingRef.current ||
      mutation.hasPendingBulkAction() ||
      !hasCurrentModeratorBulkWriteAccess(scope, store.getState())
    ) {
      return;
    }

    let cuids = selectedCuids;
    if (
      action === BULK_MEDIA_ACTIONS.UNSHARE ||
      (action === BULK_MEDIA_ACTIONS.REQUEST_REVIEW && access.isAllSharedMedia)
    ) {
      // Re-read selection and metadata at confirmation, not just on modal open.
      if (getBulkActionsSelectedItems(store.getState()) !== selectedItems) {
        return;
      }
      const data = getActiveModeratorListData(queryClient);
      const partitionSelection =
        action === BULK_MEDIA_ACTIONS.UNSHARE
          ? partitionModeratorUnshareSelection
          : partitionModeratorForceReviewSelection;
      const confirmedSelection =
        action === BULK_MEDIA_ACTIONS.UNSHARE
          ? removalSelection
          : requestReviewSelection;
      const liveSelection = partitionSelection(
        selectedCuids,
        data?.pages.flatMap((page) => page.contentItems) ?? [],
        clientId,
      );
      cuids = liveSelection.eligibleCuids;
      // Let the subscribed modal update before confirming a changed subset.
      if (
        cuids.length === 0 ||
        cuids.join('|') !== confirmedSelection.eligibleCuids.join('|')
      ) {
        return;
      }
    }

    submittingRef.current = true;
    try {
      await mutation.mutateAsync({
        action,
        scope,
        cuids,
      });
      setConfirmationAction(null);
    } catch {
      // The mutation shows the standard error toast; retain the confirmation
      // and selection so that a failed request can be retried.
    } finally {
      submittingRef.current = false;
    }
  };

  const openConfirmation = (action: ConfirmationAction) => {
    if (
      !isWriteDisabled &&
      hasCurrentModeratorBulkWriteAccess(scope, store.getState()) &&
      !(action === CONFIRMATION_ACTIONS.REMOVE && isStreamRemovalUnavailable)
    ) {
      setConfirmationAction(action);
    }
  };

  const confirm = () => {
    if (
      isWriteDisabled ||
      !hasCurrentModeratorBulkWriteAccess(scope, store.getState())
    ) {
      return;
    }

    if (confirmationAction === CONFIRMATION_ACTIONS.REQUEST_REVIEW) {
      if (noEligibleReviewMedia) {
        return;
      }
      void submit(BULK_MEDIA_ACTIONS.REQUEST_REVIEW);
    } else if (confirmationAction === CONFIRMATION_ACTIONS.REMOVE) {
      if (isStreamRemovalUnavailable || noEligibleMedia) {
        return;
      }

      void submit(
        access.isAllSharedMedia
          ? BULK_MEDIA_ACTIONS.UNSHARE
          : BULK_MEDIA_ACTIONS.REMOVE_FROM_STREAM,
      );
    }
  };

  const download = () => {
    const liveState = store.getState();
    if (
      !isWriteDisabled &&
      !downloadDisabledReason &&
      hasCurrentModeratorBulkWriteAccess(scope, liveState) &&
      getBulkActionsSelectedItems(liveState).length > 0
    ) {
      onDownload();
    }
  };

  const getConfirmationMessage = () => {
    if (confirmationAction === CONFIRMATION_ACTIONS.REQUEST_REVIEW) {
      return `Request review now for ${items}, including items without product tags?`;
    }
    if (access.isAllSharedMedia) {
      return `Remove ${items} from All Shared Media?`;
    }
    return `Remove ${items} from the ${currentCollection?.name} stream? This will not unshare the media.`;
  };

  return (
    <div className='uia-bulk-actions-icon-actions'>
      {access.isStream && (
        <SmallTooltip
          appendTarget='body'
          content={NO_PERMISSION_MESSAGE}
          isDisabled={access.canWrite}
          placement='bottom'
          target={
            <span>
              <TrackedToolbarButton
                aria-disabled={isWriteDisabled}
                className='cu-button-flat-primary bulk-actions-top-bar-button cu-mr-4'
                onClick={() => {
                  if (!isWriteDisabled) {
                    void submit(BULK_MEDIA_ACTIONS.SHARE);
                  }
                }}
                trackingId={UiaBulkActionsTrackingIds.MODERATOR_SHARE}
                type='button'
              >
                + Share {pluralize(itemCount, 'Item')}
              </TrackedToolbarButton>
            </span>
          }
        />
      )}
      <SmallTooltip
        appendTarget='body'
        content={NO_PERMISSION_MESSAGE}
        isDisabled={access.canWrite}
        placement='bottom'
        target={
          <span>
            <TrackedToolbarButton
              aria-disabled={isWriteDisabled || isStreamRemovalUnavailable}
              className='cu-button-flat-primary bulk-actions-top-bar-button cu-mr-4'
              onClick={() => openConfirmation(CONFIRMATION_ACTIONS.REMOVE)}
              trackingId={UiaBulkActionsTrackingIds.MODERATOR_REMOVE}
              type='button'
            >
              Remove {itemCount} Media
            </TrackedToolbarButton>
          </span>
        }
      />
      <ActionSheet
        actionSheetConfig={[
          {
            isDisabled: isWriteDisabled,
            label: 'Request review now',
            onClick: () =>
              openConfirmation(CONFIRMATION_ACTIONS.REQUEST_REVIEW),
            tooltipContent: !access.canWrite
              ? NO_PERMISSION_MESSAGE
              : undefined,
            trackingId: UiaBulkActionsTrackingIds.MODERATOR_REQUEST_REVIEW,
          },
          {
            isDisabled: isWriteDisabled || !!downloadDisabledReason,
            label: 'Download',
            onClick: download,
            tooltipContent: !access.canWrite
              ? NO_PERMISSION_MESSAGE
              : downloadDisabledReason || undefined,
            trackingId: UiaBulkActionsTrackingIds.MODERATOR_DOWNLOAD,
          },
        ]}
        appendTarget='body'
        placement='bottom-end'
      >
        <TrackedToolbarButton
          aria-label='More bulk actions'
          className='cu-button-invisible cu-py-2 cu-d-inline-block uia-more-button'
          disabled={mutation.isPending}
          trackingId={UiaBulkActionsTrackingIds.MODERATOR_MORE}
          type='button'
        >
          <Icon className='cu-rot-90' name='more' />
        </TrackedToolbarButton>
      </ActionSheet>
      <Modal
        closeModal={
          mutation.isPending ? undefined : () => setConfirmationAction(null)
        }
        contents={
          isAllSharedRequestReview ? (
            <RequestReviewContent selection={requestReviewSelection} />
          ) : isAllSharedRemoval ? (
            <AllSharedRemovalContent selection={removalSelection} />
          ) : (
            <p>{getConfirmationMessage()}</p>
          )
        }
        isModalOpen={confirmationAction !== null}
        primaryButtonInfo={{
          buttonContent:
            confirmationAction === CONFIRMATION_ACTIONS.REQUEST_REVIEW
              ? isAllSharedRequestReview
                ? 'Yes, request review now'
                : 'Request review now'
              : isAllSharedRemoval
                ? noEligibleMedia
                  ? 'Yes, remove from Connect'
                  : 'Yes, remove from All Shared Media'
                : 'Remove',
          isDisabled:
            isWriteDisabled ||
            noEligibleMedia ||
            noEligibleReviewMedia ||
            (confirmationAction === CONFIRMATION_ACTIONS.REMOVE &&
              isStreamRemovalUnavailable),
          onClick: confirm,
          permissionName: Permissions.ModerationView,
          trackingId: UiaBulkActionsTrackingIds.MODERATOR_CONFIRM,
        }}
        secondaryButtonInfo={{
          buttonContent: 'Cancel',
          isDisabled: mutation.isPending,
          onClick: () => setConfirmationAction(null),
          trackingId: UiaBulkActionsTrackingIds.MODERATOR_CANCEL,
        }}
        title={
          isAllSharedRemoval
            ? eligibleCount === 0
              ? "Oops! You can't do that"
              : `Remove ${eligibleCount} media from All Shared Media?`
            : confirmationAction === CONFIRMATION_ACTIONS.REQUEST_REVIEW
              ? access.isAllSharedMedia
                ? 'Are You Sure?'
                : 'Request review now?'
              : `Remove ${itemCount} media?`
        }
        width={
          isAllSharedRemoval || isAllSharedRequestReview ? 'wide' : 'medium'
        }
      />
    </div>
  );
};

type RemovalSelection = ReturnType<typeof useModeratorUnshareSelection>;
type RequestReviewSelection = ReturnType<
  typeof partitionModeratorForceReviewSelection
>;

const NoticeText = ({ children }: { children: React.ReactNode }) => (
  <p className='cu-text-ui-copy-secondary cu-mt-3'>{children}</p>
);

const RequestReviewContent = ({
  selection,
}: {
  selection: RequestReviewSelection;
}) => {
  const { eligibleCuids, reviewedCount, underReviewCount, unknownCount } =
    selection;

  if (eligibleCuids.length === 0) {
    if (unknownCount > 0) {
      return (
        <p>
          The selected media cannot be sent for review. Only media with Needs
          Product status is eligible, and some selected media statuses could
          not be verified. Refresh the page and select them again.
        </p>
      );
    }
    if (underReviewCount > 0) {
      return (
        <p>
          Only media with Needs Product status can be sent for review. The
          selected media is already under review or has been reviewed by the
          brand.
        </p>
      );
    }
    return (
      <p>The media you&apos;ve selected has already been reviewed by the brand.</p>
    );
  }
  return (
    <>
      <p>Request review on {pluralize(eligibleCuids.length, 'item')}?</p>
      {reviewedCount > 0 && (
        <NoticeText>
          {pluralize(reviewedCount, 'item')} will not be changed because{' '}
          {reviewedCount === 1 ? 'it has' : 'they have'} already been reviewed
          by the brand.
        </NoticeText>
      )}
      {underReviewCount > 0 && (
        <NoticeText>
          {pluralize(underReviewCount, 'item')} will not be changed because{' '}
          {underReviewCount === 1 ? 'it is' : 'they are'} already under review.
        </NoticeText>
      )}
      {unknownCount > 0 && (
        <NoticeText>
          {pluralize(unknownCount, 'item')} will not be changed because the
          moderation status could not be verified. Refresh the page and select
          them again.
        </NoticeText>
      )}
    </>
  );
};

const AllSharedRemovalContent = ({
  selection,
}: {
  selection: RemovalSelection;
}) => {
  const { eligibleCuids, reviewedCount, unknownCount } = selection;

  if (eligibleCuids.length === 0) {
    return (
      <p>
        {unknownCount > 0
          ? "The media you've selected cannot be removed. Only media with status Under Review or Needs Product can be removed, and some selected media statuses could not be verified. Refresh the page and select them again."
          : `The media you've selected cannot be removed from Connect because ${reviewedCount === 1 ? 'it has' : 'they have'} already been reviewed by the brand.`}
      </p>
    );
  }
  return (
    <>
      <p>
        Media that you remove from All Shared Media will be removed from this
        page and from review by the brand, and returned to your Media Streams.
      </p>
      {reviewedCount > 0 && (
        <Alert type='warning'>
          {reviewedCount} media won&apos;t be removed because{' '}
          {reviewedCount === 1 ? "it's" : "they've"} already been reviewed by
          the brand.
        </Alert>
      )}
      {unknownCount > 0 && (
        <Alert className='cu-mt-3' type='warning'>
          {unknownCount} media won&apos;t be removed because their moderation
          status could not be verified. Refresh the page and select them again.
        </Alert>
      )}
    </>
  );
};

const TrackedToolbarButton = withTracking('button');

angular.module('curalate').component('moderatorBulkActions', {
  bindings: {
    onDownload: '&',
    downloadDisabledReason: '<',
  },
  controller: makeAngularReactControllerWithScope(
    (scope: IScope & { $ctrl: Props }) => (
      <ModeratorBulkActions
        {...scope.$ctrl}
        onDownload={
          applyScope(scope, scope.$ctrl.onDownload) as Props['onDownload']
        }
      />
    ),
  ),
});
